import React from "react";
import { useRouter } from "next/router";
import { STNewsDetail, STHead, STButton } from "@/components";
import { getBlogBySlug, getPaginatedBlogs } from "@/actions";

const sanitizeBlogForPage = (blog) => {
  if (!blog) return blog;

  const sanitized = { ...blog };
  const longDescription = sanitized.long_description;

  if (typeof longDescription === "string" && longDescription.trim()) {
    try {
      const parsed = JSON.parse(longDescription);

      const sanitizeNode = (node) => {
        if (Array.isArray(node)) {
          return node.map(sanitizeNode).filter(Boolean);
        }

        if (!node || typeof node !== "object") return node;

        if (node.entityMap && typeof node.entityMap === "object") {
          const nextEntityMap = {};
          for (const [key, value] of Object.entries(node.entityMap)) {
            const src = value?.data?.src;
            if (typeof src === "string" && src.startsWith("data:")) continue;
            nextEntityMap[key] = value;
          }
          node.entityMap = nextEntityMap;
        }

        if (node.content) {
          node.content = sanitizeNode(node.content);
        }

        if (node.blocks) {
          node.blocks = node.blocks.filter((block) => {
            const entityRanges = block?.entityRanges || [];
            return !entityRanges.some((range) => {
              const entity = node.entityMap?.[range.key];
              const src = entity?.data?.src;
              return typeof src === "string" && src.startsWith("data:");
            });
          });
        }

        return node;
      };

      sanitized.long_description = JSON.stringify(sanitizeNode(parsed));
    } catch (error) {
      sanitized.long_description = longDescription.replace(/data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+/g, "");
    }
  }

  return sanitized;
};

export async function getStaticPaths() {
  let paths = [];
  try {
    const response = await getPaginatedBlogs(1, 20);
    if (response?.status === 200 && response.data?.data) {
      paths = response.data.data.map((blog) => ({
        params: { slug: blog.slug },
      }));
    }
  } catch (error) {
    console.error("Error fetching blog paths:", error);
  }

  return {
    paths,
    fallback: "blocking",
  };
}

export async function getStaticProps(context) {
  const { slug } = context.params;

  try {
    const response = await getBlogBySlug(slug);
    if (response?.status === 200 && response.data) {
      return {
        props: {
          blog: sanitizeBlogForPage(response.data),
          slug,
        },
        revalidate: 300,
      };
    }
  } catch (error) {
    console.error("Error fetching blog:", error);
  }

  return {
    props: {
      blog: null,
      slug,
    },
    revalidate: 60,
  };
}

const NewsDetailPage = ({ blog, slug }) => {
  const router = useRouter();

  if (!blog) {
    return (
      <div className="min-vh-100 py-200 text-center default-page-wrap d-flex flex-column justify-content-center align-items-center bg-gradient-primary not-found">
        <div className="container">
          <h1 className="primary-title text-white">News not found</h1>

          <p className="paragraph-text mb-4 text-clr-white-55">
            The news article you are looking for does not exist.
          </p>

          <STButton
            onClick={() =>
              router.push(
                `/news-feed${router.query.tab ? `?tab=${router.query.tab}` : ""}`
              )
            }
          >
            Back to News Feed
          </STButton>
        </div>
      </div>
    );
  }

  return (
    <>
      <STHead
        title={blog.name || blog.title}
        description={
          blog?.meta_description ||
          blog?.short_description ||
          "Read the latest news and insights from Sustanix."
        }
        keywords={
          blog?.meta_keywords ||
          "green chemistry, biomass coatings, sustainable additives, toxic-free materials, biodegradable, PFAS free, microplastic free, industrial coatings, circular materials, paper packaging, bio-based coatings, VOCs free, sustainable chemistry"
        }
        canonicalUrl={`https://sustanix.com/news-feed/${slug}/`}
        ogTitle={`${blog?.name || blog?.title} | Sustanix | ${blog?.category === "blog" ? "Blogs" : "News"}`}
        ogDescription={
          blog?.meta_description ||
          blog?.short_description ||
          "Read the latest news and insights from Sustanix."
        }
        ogImage={blog?.image ? `https://api.sustanix.com/api/v1/common/process-image?url=${blog.image}&q=80&w=860&h=456` : ""}
        ogType="article"
        datePublished={blog.published_at || blog.created_at}
        dateModified={blog.updated_at || blog.created_at}
      />
      <STNewsDetail newsItem={blog} />
    </>
  );
};

export default NewsDetailPage;
